<script setup lang="ts">
	import axios from '@/lib/axios'
	import { PlusIcon } from '@heroicons/vue/24/solid'
	import { useQuery } from '@tanstack/vue-query'
	import { refDebounced } from '@vueuse/core'
	import { computed, ref, watch } from 'vue'
	import { useRouter } from 'vue-router'

	import HasPermission from '@/components/HasPermission.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useCompany from '@/modules/companies/composables/useCompany'
	import type { Company } from '@/modules/companies/interfaces/company'
	import GroupEntry from '@/modules/groups/components/GroupEntry.vue'
	import { useCompanyGroups } from '@/modules/groups/composables/useCompanyGroups'
	import PlanComparisonModal from '@/modules/plans/components/PlanComparisonModal.vue'
	import usePlanFeature from '@/modules/plans/composables/usePlanFeature'
	import useFeatureLockCopy from '@/modules/plans/composables/useFeatureLockCopy'

	const { user } = useAuthUser()
	const router = useRouter()

	// Company selector (internal users)
	const companySearch = ref('')
	const showCompanyDropdown = ref(false)
	const debouncedCompanySearch = refDebounced(companySearch, 300)

	const { data: companyResults } = useQuery({
		queryKey: computed(() => [
			'companySearch-groups',
			debouncedCompanySearch.value,
		]),
		queryFn: async () => {
			const { data } = await axios.get<Company[]>('api/companies/search', {
				params: { q: debouncedCompanySearch.value },
			})
			return data
		},
		enabled: computed(
			() => debouncedCompanySearch.value.length >= 2 && !!user.value?.internal
		),
		staleTime: 30_000,
	})

	function onCompanyBlur() {
		setTimeout(() => {
			showCompanyDropdown.value = false
		}, 150)
	}

	function selectCompany(c: Company) {
		companySearch.value = c.name ?? ''
		showCompanyDropdown.value = false
		setCompany(String(c.id))
	}

	// Groups data
	const {
		data: groups,
		isLoading,
		isFetching,
		setCompany,
		setPage,
		searchByCompany,
		pagination,
		page,
		companyId,
	} = useCompanyGroups({ immediate: true })

	const { company, refetch: refetchCompany } = useCompany(
		{ companyId: companyId.value },
		{ immediate: !user.value?.internal }
	)

	watch(companyId, () => refetchCompany())

	watch(
		() => companyId.value,
		() => {
			setPage('1')
			searchByCompany()
		},
		{ immediate: true }
	)

	// Client-side name filter
	const nameFilter = ref('')
	const debouncedNameFilter = refDebounced(nameFilter, 300)
	const filteredGroups = computed(() => {
		if (!groups.value) return []
		const q = debouncedNameFilter.value.toLowerCase().trim()
		if (!q) return groups.value
		return groups.value.filter((g) => g.name.toLowerCase().includes(q))
	})

	const handlePage = (value: string) => {
		const n = Number(value)
		if (n >= 1 && n <= (pagination.value?.meta.lastPage ?? 1)) {
			setPage(value)
			searchByCompany()
		}
	}

	// Grupos y políticas de viaje: función de los planes PRO y BUSINESS+ (el
	// backend responde 422 en todo el módulo). Con plan bloqueado la lista queda
	// con blur + overlay que menciona los planes y abre el modal de comparación.
	// El candado sigue a la empresa que se está viendo (selector interno usable).
	const { hasPermissionUser } = useHasPermissionUser()
	const {
		locked: planLocked,
		subscriptionId,
		currentPlanCode,
	} = usePlanFeature(companyId, 'travel_policies')
	const { lockTitle, lockDescription } = useFeatureLockCopy('travel_policies')
	const showCompare = ref(false)
	const canManageBilling = computed(() =>
		hasPermissionUser('update', 'App\\Models\\Company')
	)

	// El swap/pago real vive en la página de suscripción de la empresa; el modal
	// solo elige el plan y ahí se le pasa la mano.
	function onSelectPlan(planCode: string) {
		if (!companyId.value) return
		router.push({
			name: 'company.subscriptions',
			params: { companyId: String(companyId.value) },
			query: { planCode },
		})
	}
</script>

<template>
	<div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		<!-- Header -->
		<div class="flex items-start justify-between mb-6">
			<div>
				<h1 class="text-2xl font-bold text-gray-900">Grupos y políticas</h1>
				<p class="text-sm text-gray-500 mt-1">
					{{
						company?.name ?
							`Grupos de ${company.name}`
						:	'Gestiona los grupos y políticas de viaje'
					}}
				</p>
			</div>
			<HasPermission
				name-permission="create-groups"
				:entity-type="'App\\Models\\Company'"
			>
				<RouterLink
					:to="{ name: 'groups.create' }"
					class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#021F58] text-white text-sm font-medium hover:bg-[#032580] transition-colors flex-shrink-0"
				>
					<PlusIcon class="h-4 w-4" />
					<span class="hidden sm:inline">Nuevo grupo</span>
					<span class="sm:hidden">Nuevo</span>
				</RouterLink>
			</HasPermission>
		</div>

		<!-- Company selector (internal) -->
		<div
			v-if="user?.internal"
			class="mb-4 bg-white rounded-xl border border-gray-200 p-4"
		>
			<p
				class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
			>
				Empresa
			</p>
			<div class="relative">
				<input
					v-model="companySearch"
					type="text"
					placeholder="Buscar empresa por nombre..."
					class="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-400"
					@focus="showCompanyDropdown = true"
					@blur="onCompanyBlur"
				/>
				<div
					v-if="showCompanyDropdown && companyResults?.length"
					class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-auto"
				>
					<button
						v-for="c in companyResults"
						:key="c.id"
						type="button"
						class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
						@mousedown.prevent="selectCompany(c)"
					>
						{{ c.name }}
					</button>
				</div>
			</div>
		</div>

		<!-- Plan sin grupos/políticas (free/start): blur + overlay de upsell -->
		<div :class="planLocked ? 'relative min-h-[320px]' : ''">
			<div
				v-if="planLocked"
				class="absolute inset-0 z-20 flex flex-col items-center justify-center text-center gap-3 rounded-xl bg-white/60 backdrop-blur-[6px] p-8"
			>
				<span class="material-symbols-outlined text-5xl text-gray-500"
					>lock</span
				>
				<h2 class="text-xl font-bold text-gray-900 max-w-lg">
					{{ lockTitle }}
				</h2>
				<p
					v-if="lockDescription"
					class="text-sm text-gray-600 max-w-md"
				>
					{{ lockDescription }}
				</p>
				<button
					class="mt-2 px-4 py-2 rounded-lg bg-[#021F58] text-white text-sm font-medium hover:opacity-90 transition-opacity"
					@click="showCompare = true"
				>
					Ver planes
				</button>
			</div>

			<div
				:class="planLocked ? 'pointer-events-none select-none' : ''"
				:aria-hidden="planLocked"
			>
				<!-- Search bar -->
				<div class="mb-4">
					<input
						v-model="nameFilter"
						type="text"
						placeholder="Buscar por nombre de grupo..."
						class="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-400 w-full sm:w-72"
					/>
				</div>

				<!-- Internal: no company selected -->
				<div
					v-if="user?.internal && !companyId"
					class="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200"
				>
					<span class="material-symbols-outlined text-5xl text-gray-200 mb-3"
						>group</span
					>
					<p class="text-sm font-medium text-gray-500">
						Selecciona una empresa para ver sus grupos
					</p>
				</div>

				<template v-else>
					<!-- Skeleton -->
					<div
						v-if="isLoading"
						class="space-y-2 animate-pulse"
					>
						<div
							v-for="i in 5"
							:key="i"
							class="h-14 rounded-xl bg-gray-100"
						/>
					</div>

					<!-- Table -->
					<div
						v-else
						class="bg-white rounded-xl border border-gray-200 overflow-hidden"
						:class="isFetching ? 'opacity-60 pointer-events-none' : ''"
					>
						<!-- Desktop header -->
						<div
							class="hidden sm:grid sm:grid-cols-4 px-4 py-3 border-b border-gray-100 bg-gray-50"
						>
							<p
								class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
							>
								Grupo
							</p>
							<p
								class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center"
							>
								Políticas
							</p>
							<p
								class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center"
							>
								Integrantes
							</p>
							<p
								class="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center"
							>
								Acciones
							</p>
						</div>

						<!-- Empty state -->
						<div
							v-if="!filteredGroups.length"
							class="flex flex-col items-center justify-center py-16"
						>
							<span
								class="material-symbols-outlined text-4xl text-gray-200 mb-3"
								>group_off</span
							>
							<p class="text-sm font-medium text-gray-500">
								{{
									nameFilter ?
										`Sin resultados para "${nameFilter}"`
									:	'No hay grupos registrados'
								}}
							</p>
						</div>

						<!-- Group rows -->
						<GroupEntry
							v-for="group in filteredGroups"
							:key="group.id"
							:group="group"
							:user="user!"
							:company-search="Number(companyId)"
						/>

						<!-- Pagination -->
						<div
							v-if="pagination?.meta && pagination.meta.lastPage > 1"
							class="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50"
						>
							<p class="text-xs text-gray-400">
								{{ pagination.meta.from }}–{{ pagination.meta.to }} de
								{{ pagination.meta.total }} grupos
							</p>
							<div class="flex items-center gap-1">
								<button
									type="button"
									:disabled="Number(page) <= 1"
									class="px-2.5 py-1 rounded-lg text-xs border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									@click="handlePage((Number(page) - 1).toString())"
								>
									Anterior
								</button>
								<span class="px-3 text-xs text-gray-500"
									>{{ page }} / {{ pagination.meta.lastPage }}</span
								>
								<button
									type="button"
									:disabled="Number(page) >= pagination.meta.lastPage"
									class="px-2.5 py-1 rounded-lg text-xs border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									@click="handlePage((Number(page) + 1).toString())"
								>
									Siguiente
								</button>
							</div>
						</div>
					</div>
				</template>
			</div>

			<PlanComparisonModal
				v-model:open="showCompare"
				:company-id="Number(companyId) || undefined"
				:subscription-id="subscriptionId"
				:current-plan-code="currentPlanCode"
				:can-manage="canManageBilling"
				@select="onSelectPlan"
			/>
		</div>
	</div>
</template>
